import { create } from 'zustand';
import type {
  InspectionWizardState,
  Vehicle,
  TireImage,
  TireResult,
  Inspection,
  TireStatus,
} from '../types';

// Brand-specific tire data for smarter recommendations
export const TIRE_BRANDS = [
  'Michelin',
  'Bridgestone',
  'Goodyear',
  'Continental',
  'Pirelli',
  'Hankook',
  'BFGoodrich',
  'Firestone',
  'Yokohama',
  'Cooper',
  'Other',
] as const;

export type TireBrand = typeof TIRE_BRANDS[number];

export interface BrandSpecs {
  initialTread: number;      // New tire tread depth in mm
  expectedLifeMiles: number; // Expected mileage
  warrantyYears: number;     // Typical warranty period
  tier: 'premium' | 'mid' | 'budget';
  alternatives: string[];    // Comparable brands to recommend
}

export const BRAND_SPECS: Record<string, BrandSpecs> = {
  'Michelin': {
    initialTread: 10.0,
    expectedLifeMiles: 65000,
    warrantyYears: 6,
    tier: 'premium',
    alternatives: ['Continental', 'Pirelli', 'Bridgestone'],
  },
  'Bridgestone': {
    initialTread: 10.0,
    expectedLifeMiles: 60000,
    warrantyYears: 5,
    tier: 'premium',
    alternatives: ['Michelin', 'Continental', 'Goodyear'],
  },
  'Goodyear': {
    initialTread: 11.0,
    expectedLifeMiles: 55000,
    warrantyYears: 6,
    tier: 'premium',
    alternatives: ['Michelin', 'Bridgestone', 'BFGoodrich'],
  },
  'Continental': {
    initialTread: 10.0,
    expectedLifeMiles: 60000,
    warrantyYears: 6,
    tier: 'premium',
    alternatives: ['Michelin', 'Pirelli', 'Bridgestone'],
  },
  'Pirelli': {
    initialTread: 9.5,
    expectedLifeMiles: 50000,
    warrantyYears: 5,
    tier: 'premium',
    alternatives: ['Michelin', 'Continental', 'Bridgestone'],
  },
  'Hankook': {
    initialTread: 10.0,
    expectedLifeMiles: 50000,
    warrantyYears: 5,
    tier: 'mid',
    alternatives: ['Yokohama', 'Cooper', 'Firestone'],
  },
  'BFGoodrich': {
    initialTread: 11.0,
    expectedLifeMiles: 55000,
    warrantyYears: 6,
    tier: 'mid',
    alternatives: ['Goodyear', 'Cooper', 'Firestone'],
  },
  'Firestone': {
    initialTread: 10.0,
    expectedLifeMiles: 50000,
    warrantyYears: 5,
    tier: 'mid',
    alternatives: ['Hankook', 'Cooper', 'BFGoodrich'],
  },
  'Yokohama': {
    initialTread: 10.0,
    expectedLifeMiles: 50000,
    warrantyYears: 5,
    tier: 'mid',
    alternatives: ['Hankook', 'Cooper', 'Firestone'],
  },
  'Cooper': {
    initialTread: 10.0,
    expectedLifeMiles: 45000,
    warrantyYears: 5,
    tier: 'budget',
    alternatives: ['Hankook', 'Firestone', 'Yokohama'],
  },
};

interface InspectionStore extends InspectionWizardState {
  tireBrand: string | null;
  setCurrentStep: (step: number) => void;
  setVehicle: (vehicle: Vehicle) => void;
  setTireBrand: (brand: string | null) => void;
  addTireImage: (image: TireImage) => void;
  removeTireImage: (position: string) => void;
  setTireResults: (results: TireResult[]) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  setInspection: (inspection: Inspection) => void;
  resetWizard: () => void;
  nextStep: () => void;
  previousStep: () => void;
}

const initialState: InspectionWizardState & { tireBrand: string | null } = {
  currentStep: 0,
  vehicle: null,
  tireBrand: null,
  tireImages: [],
  tireResults: [],
  isAnalyzing: false,
  inspection: null,
};

export const useInspectionStore = create<InspectionStore>((set) => ({
  ...initialState,
  setCurrentStep: (step) => set({ currentStep: step }),
  setVehicle: (vehicle) => set({ vehicle }),
  setTireBrand: (brand) => set({ tireBrand: brand }),
  addTireImage: (image) =>
    set((state) => {
      const filtered = state.tireImages.filter((img) => img.position !== image.position);
      return { tireImages: [...filtered, image] };
    }),
  removeTireImage: (position) =>
    set((state) => ({
      tireImages: state.tireImages.filter((img) => img.position !== position),
    })),
  setTireResults: (results) => set({ tireResults: results }),
  setIsAnalyzing: (isAnalyzing) => set({ isAnalyzing }),
  setInspection: (inspection) => set({ inspection }),
  resetWizard: () => set(initialState),
  nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),
  previousStep: () =>
    set((state) => ({ currentStep: Math.max(0, state.currentStep - 1) })),
}));

// Helper functions for inspection logic
export const determineOverallStatus = (tireResults: TireResult[]): TireStatus => {
  if (tireResults.some((t) => t.status === 'unsafe')) return 'unsafe';
  if (tireResults.some((t) => t.status === 'replace_soon')) return 'replace_soon';
  if (tireResults.some((t) => t.status === 'watch')) return 'watch';
  return 'ok';
};

export const generateRecommendations = (
  tireResults: TireResult[],
  tireBrand?: string | null
): string[] => {
  const recommendations: string[] = [];
  const brandSpecs = tireBrand && tireBrand !== 'Other' ? BRAND_SPECS[tireBrand] : null;
  const needsReplacement = tireResults.some((t) => t.tread_mm < 3.0 || t.status === 'unsafe' || t.status === 'replace_soon');

  // Check for unsafe tread depth
  const unsafeTires = tireResults.filter((t) => t.tread_mm < 2.0);
  if (unsafeTires.length > 0) {
    recommendations.push(
      `Immediate replacement recommended for ${unsafeTires.map((t) => t.position).join(', ')} tire(s) - tread depth below 2.0mm is unsafe.`
    );
  }

  // Check for replace soon
  const replaceSoonTires = tireResults.filter(
    (t) => t.tread_mm >= 2.0 && t.tread_mm < 3.0
  );
  if (replaceSoonTires.length > 0) {
    recommendations.push(
      `Schedule replacement soon for ${replaceSoonTires.map((t) => t.position).join(', ')} tire(s) - tread depth below 3.0mm.`
    );
  }

  // Check for tire age
  const oldTires = tireResults.filter((t) => t.age_years > 6.0);
  if (oldTires.length > 0) {
    recommendations.push(
      `Replace ${oldTires.map((t) => t.position).join(', ')} tire(s) - age exceeds 6 years, beyond recommended limit.`
    );
  }

  // Check for alignment issues (front tires differ significantly)
  const frontLeft = tireResults.find((t) => t.position === 'FL');
  const frontRight = tireResults.find((t) => t.position === 'FR');
  if (frontLeft && frontRight && Math.abs(frontLeft.tread_mm - frontRight.tread_mm) > 1.5) {
    recommendations.push(
      'Front tires show significant tread depth difference - possible alignment issue. Have alignment checked.'
    );
  }

  // Check for uneven wear patterns
  const unevenWear = tireResults.filter(
    (t) => t.wear_pattern !== 'even'
  );
  if (unevenWear.length > 0) {
    recommendations.push(
      `Uneven wear detected on ${unevenWear.map((t) => t.position).join(', ')} tire(s) - check alignment, tire pressure, and suspension.`
    );
  }

  // Brand-specific recommendations
  if (brandSpecs && needsReplacement) {
    // Suggest same brand or alternatives based on tier
    const tierLabel = brandSpecs.tier === 'premium' ? 'Premium' : brandSpecs.tier === 'mid' ? 'Mid-tier' : 'Budget-friendly';
    recommendations.push(
      `${tierLabel} replacement options: ${tireBrand} or comparable brands like ${brandSpecs.alternatives.slice(0, 2).join(', ')}.`
    );
  }

  // Brand-specific tread life estimate
  if (brandSpecs && !needsReplacement) {
    const avgTread = tireResults.reduce((sum, t) => sum + t.tread_mm, 0) / tireResults.length;
    const wearPercent = ((brandSpecs.initialTread - avgTread) / brandSpecs.initialTread) * 100;
    const remainingLifePercent = Math.max(0, 100 - wearPercent);
    const estimatedRemainingMiles = Math.round((remainingLifePercent / 100) * brandSpecs.expectedLifeMiles);

    if (estimatedRemainingMiles > 0) {
      recommendations.push(
        `Based on ${tireBrand} specifications: ~${estimatedRemainingMiles.toLocaleString()} miles of tread life remaining (${Math.round(remainingLifePercent)}% remaining).`
      );
    }
  }

  // Default recommendation if all is well
  if (recommendations.length === 0) {
    recommendations.push(
      'All tires are in good condition. Continue regular maintenance and inspections.'
    );
  }

  return recommendations;
};
